/**
 * XOT shared storage — canonical TTL-aware localStorage interface.
 *
 * Two surfaces:
 *   1. Named ESM exports — for unit tests + future ES module consumers.
 *   2. Side-effect on import: attaches `window.XOT.storage` for the legacy
 *      IIFE consumers (single-article.js, xot-podcast-player.js) that still
 *      execute before they're migrated to ESM in Phase 2.
 *
 * Build path:
 *   src                      assets/js/src/lib/xot-storage.ts
 *   ↓ esbuild (IIFE, target ES2020)
 *   dist                     assets/dist/js/xot-storage.js   (auto-attaches on load)
 *
 * Test path:
 *   src                      assets/js/src/lib/xot-storage.ts
 *   ↓ vitest (transforms TS, jsdom env)
 *   test                     tests/js/xot-storage.test.ts    (imports named exports)
 *
 * Key namespace: `xot.v1.*`. Versioning the prefix means a future
 * incompatible payload change ships under `xot.v2.*` with a migration
 * pass, keeping old + new co-resident during the transition.
 */

const NS = 'xot.v1.';
const DAY_MS = 86_400_000;
const DEFAULT_TTL_MS = 30 * DAY_MS;

export interface StoredPayload<T> {
    readonly v: T;
    readonly at: number;
    readonly ttl: number;
}

export type StoragePositionScope = 'tts' | 'episode';

/**
 * Build a namespaced key from a scope + id + optional modifiers. Empty
 * modifier segments are filtered so `key('pos','tts',123)` and
 * `key('pos','tts',123, null)` produce the same key.
 */
export function buildKey(scope: string, id: string | number, ...mods: Array<string | number | null | undefined>): string {
    const parts: Array<string | number> = [scope, id];
    for (const m of mods) {
        if (m === undefined || m === null || m === '') continue;
        parts.push(m);
    }
    return NS + parts.join(':');
}

/**
 * Set a value with TTL. Silent on quota / disabled localStorage — never
 * throws to callers (write-failure is recoverable; we don't want one bad
 * write to crash an unrelated UI path).
 */
export function set<T>(key: string, value: T, ttlMs?: number): void {
    const ttl = typeof ttlMs === 'number' && ttlMs > 0 ? ttlMs : DEFAULT_TTL_MS;
    try {
        const payload: StoredPayload<T> = { v: value, at: Date.now(), ttl };
        localStorage.setItem(key, JSON.stringify(payload));
    } catch {
        /* QuotaExceeded / disabled — silent */
    }
}

/**
 * Read a value. Returns null on missing, expired, or corrupt. Expired
 * entries are evicted on read so they don't accumulate over time.
 */
export function get<T = unknown>(key: string): T | null {
    try {
        const raw = localStorage.getItem(key);
        if (!raw) return null;
        const o = JSON.parse(raw) as Partial<StoredPayload<T>>;
        if (!o || typeof o !== 'object') return null;
        if (typeof o.at !== 'number' || typeof o.ttl !== 'number') return null;
        if (Date.now() - o.at > o.ttl) {
            try { localStorage.removeItem(key); } catch { /* silent */ }
            return null;
        }
        return 'v' in o ? (o.v as T) : null;
    } catch {
        return null;
    }
}

export function del(key: string): void {
    try { localStorage.removeItem(key); } catch { /* silent */ }
}

/**
 * Position helpers — playback resume for any audio source.
 *
 * Convention:
 *   scope    = 'tts' | 'episode'
 *   id       = post id (TTS) | guid hash (episode)
 *   modifier = voice preference (TTS) | null (episode)
 *
 * Returns 0 (not null) for misses so callers can `if (pos > 2)` cleanly.
 */
export const position = {
    save(scope: StoragePositionScope, id: string | number, modifier: string | null, seconds: number): void {
        if (typeof seconds !== 'number' || !Number.isFinite(seconds) || seconds < 2) return;
        set(buildKey('pos', scope, id, modifier), seconds);
    },
    load(scope: StoragePositionScope, id: string | number, modifier: string | null): number {
        const v = get<number>(buildKey('pos', scope, id, modifier));
        return typeof v === 'number' && Number.isFinite(v) && v >= 0 ? v : 0;
    },
    clear(scope: StoragePositionScope, id: string | number, modifier: string | null): void {
        del(buildKey('pos', scope, id, modifier));
    },
};

/**
 * One-time migration of legacy keys. Safe to remove after telemetry shows
 * zero hits on the legacy patterns.
 *
 * Legacy patterns:
 *   xot_tts_pos_<postId>_<voice>   →  xot.v1.pos:tts:<postId>:<voice>
 *   xot:ep:<guidHash>              →  xot.v1.pos:episode:<guidHash>
 */
export function migrateLegacy(): void {
    try {
        const len = localStorage.length;
        for (let i = 0; i < len; i++) {
            const k = localStorage.key(i);
            if (!k) continue;

            if (k.startsWith('xot_tts_pos_')) {
                const rest = k.slice('xot_tts_pos_'.length);
                const sep = rest.lastIndexOf('_');
                if (sep > 0) {
                    const pid = rest.slice(0, sep);
                    const voice = rest.slice(sep + 1);
                    try {
                        const raw = localStorage.getItem(k);
                        const o = raw ? (JSON.parse(raw) as { t?: number }) : null;
                        const t = o && typeof o.t === 'number' ? o.t : null;
                        if (t !== null && t >= 2) {
                            position.save('tts', pid, voice, t);
                        }
                    } catch { /* silent */ }
                    try { localStorage.removeItem(k); } catch { /* silent */ }
                }
                continue;
            }

            if (k.startsWith('xot:ep:')) {
                const guid = k.slice('xot:ep:'.length);
                if (guid) {
                    try {
                        const raw = localStorage.getItem(k);
                        const sec = raw ? Number.parseFloat(raw) : Number.NaN;
                        if (Number.isFinite(sec) && sec >= 2) {
                            position.save('episode', guid, null, sec);
                        }
                    } catch { /* silent */ }
                    try { localStorage.removeItem(k); } catch { /* silent */ }
                }
            }
        }
    } catch {
        /* localStorage disabled — silent */
    }
}

/* Public API surface — the same shape exposed on `window.XOT.storage`. */
export const storage = Object.freeze({
    key: buildKey,
    set,
    get,
    del,
    position: Object.freeze(position),
    NS,
});

/* ─── Side-effect on import: attach to window.XOT for legacy IIFE
   consumers + run one-time legacy key migration. */
declare global {
    interface Window {
        XOT?: { storage?: typeof storage; [k: string]: unknown };
    }
}

if (typeof window !== 'undefined') {
    const w = window as Window;
    w.XOT = w.XOT ?? Object.create(null);
    if (!w.XOT!.storage) {
        w.XOT!.storage = storage;
        migrateLegacy();
    }
}
